/**
 * MaVCardQuickAccess — Accès rapide à la carte de visite digitale.
 *
 * Composant réutilisable utilisé sur :
 *   - /admin/ma-carte          (page dédiée admin)
 *   - /moniteur/ma-carte       (page dédiée enseignant)
 *   - /admin/dashboard         (encart compact en haut de page)
 *   - /moniteur/ma-fiche       (section secondaire)
 *
 * Props :
 *   compact  : bool  → version compacte (dashboard) sans la liste des prospects
 *   title    : string → titre custom
 */
import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import {
  Contact, Copy, Check, Share2, ExternalLink, Download, Printer,
  Eye, Calendar as CalendarIcon, QrCode, BarChart3, Phone, Mail,
  Sparkles, ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';

export default function MaVCardQuickAccess({ compact = false, title }) {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const vcardUrl = `${origin}/vcard`;
  const qrRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  // Charger la liste des prospects suivis (pour agréger les stats)
  const loadStats = useCallback(async () => {
    try {
      const d = await api.vcardList();
      const items = d?.items || [];
      // Agrège toutes les stats
      const total = items.reduce(
        (acc, it) => ({
          visits:   acc.visits   + (it?.stats?.visits   || 0),
          calls:    acc.calls    + (it?.stats?.calls    || 0),
          reserves: acc.reserves + (it?.stats?.reserves || it?.stats?.cal_reserves || 0),
          clicks:   acc.clicks   + (it?.stats?.clicks   || 0),
        }),
        { visits: 0, calls: 0, reserves: 0, clicks: 0 }
      );
      setStats({ ...total, count: items.length });
      // Top 3 des plus actifs
      const sorted = [...items].sort((a, b) =>
        ((b?.stats?.visits || 0) + (b?.stats?.clicks || 0))
        - ((a?.stats?.visits || 0) + (a?.stats?.clicks || 0))
      );
      setRecent(sorted.slice(0, 3));
    } catch (_) { /* silent */ }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(vcardUrl);
      setCopied(true);
      toast.success('Lien de ma carte copié', {
        description: 'Collez-le dans un SMS, WhatsApp ou email',
      });
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      toast.error('Impossible de copier');
    }
  };

  const share = async () => {
    const data = {
      title: 'Ma carte de visite — Bruno Llorens',
      text: 'Enseignant de la conduite — Moto & Auto · Code2Roues · Disponible 7j/7',
      url: vcardUrl,
    };
    if (navigator.share) {
      try { await navigator.share(data); }
      catch (_) { /* user cancelled */ }
    } else {
      copyLink();
    }
  };

  const downloadQr = () => {
    if (!qrRef.current) return;
    const canvas = qrRef.current.querySelector('canvas');
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = 'carte-visite-bruno-llorens-code2roues.png';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success('QR téléchargé');
  };

  const printCard = () => {
    if (!qrRef.current) return;
    const canvas = qrRef.current.querySelector('canvas');
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');
    const html = `
<!doctype html><html><head><meta charset="utf-8"><title>Ma carte de visite · Bruno Llorens</title>
<style>
  @page { size: A6; margin: 10mm; }
  body { margin:0; padding:28px 24px; font-family:'Playfair Display',Georgia,serif; background:#0f172a; color:#fff; text-align:center; }
  .brand { font-size:9px; letter-spacing:0.3em; color:#fb923c; text-transform:uppercase; margin-bottom:4px; font-weight:900; font-family:system-ui,sans-serif; }
  h1 { font-size:24px; font-weight:900; margin:0 0 2px; }
  .role { color:#fdba74; font-size:12px; margin-bottom:14px; font-family:system-ui,sans-serif; }
  .phone { font-size:22px; font-weight:900; margin:10px 0; font-family:system-ui,sans-serif; }
  .qr { display:inline-block; padding:14px; border-radius:14px; background:#fff; }
  .cta { font-size:11px; letter-spacing:0.14em; text-transform:uppercase; color:#fdba74; margin-top:14px; font-weight:800; font-family:system-ui,sans-serif; }
  .url { font-size:9px; color:#94a3b8; margin-top:6px; font-family:monospace; word-break:break-all; }
</style></head>
<body>
  <div class="brand">code2roues.fr</div>
  <h1>Llorens Bruno</h1>
  <div class="role">Enseignant de la conduite — Moto & Auto · ECSR + CCS Deux Roues</div>
  <div class="phone">+33 7 82 26 82 00</div>
  <div class="qr"><img src="${dataUrl}" width="200" height="200" alt="QR" /></div>
  <div class="cta">Scannez pour tout savoir</div>
  <div class="url">${vcardUrl}</div>
</body></html>`;
    const w = window.open('', '_blank', 'width=600,height=800');
    if (!w) { toast.error('Popup bloquée — autorisez-la puis réessayez'); return; }
    w.document.open(); w.document.write(html); w.document.close();
    setTimeout(() => w.print(), 400);
  };

  // ───────────────────────────────────────────────────────── VERSION COMPACTE (Dashboard)
  if (compact) {
    return (
      <section
        className="relative overflow-hidden rounded-2xl border border-orange-500/30 bg-gradient-to-br from-orange-500/[0.10] via-orange-500/[0.03] to-transparent p-4 sm:p-5"
        data-testid="ma-vcard-quick-compact"
      >
        <div className="flex items-start gap-4">
          {/* Mini QR */}
          <div ref={qrRef} className="shrink-0 bg-white p-2 rounded-xl shadow-lg hidden sm:block">
            <QRCodeCanvas value={vcardUrl} size={88} level="M" includeMargin={false} />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Contact className="w-4 h-4 text-orange-400" />
              <span className="text-[10.5px] font-black uppercase tracking-[0.22em] text-orange-400">
                Ma carte de visite
              </span>
            </div>
            <h3
              className="text-xl sm:text-2xl font-black text-foreground tracking-tight mb-1"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Partagez-la en 1 clic
            </h3>
            <p className="text-xs text-muted-foreground mb-3">
              {stats && stats.visits > 0
                ? `${stats.visits} visite${stats.visits > 1 ? 's' : ''} enregistrée${stats.visits > 1 ? 's' : ''} sur vos liens prospects.`
                : 'Montrez le QR ou envoyez le lien à vos prospects.'}
            </p>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={share}
                data-testid="ma-vcard-share-compact"
                className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold transition shadow-md shadow-orange-900/30"
              >
                <Share2 className="w-3.5 h-3.5" /> Partager
              </button>
              <button
                onClick={copyLink}
                data-testid="ma-vcard-copy-compact"
                className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] text-foreground text-xs font-semibold transition"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? 'Copié !' : 'Copier le lien'}
              </button>
              <Link
                to="/admin/ma-carte"
                data-testid="ma-vcard-open-compact"
                className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] text-foreground text-xs font-semibold transition"
              >
                Détails <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // ───────────────────────────────────────────────────────── VERSION PLEINE (page dédiée)
  return (
    <div className="max-w-5xl" data-testid="ma-vcard-page">
      {/* Hero */}
      <div className="mb-6">
        <div className="text-[10.5px] font-black uppercase tracking-[0.22em] text-orange-500 flex items-center gap-2 mb-2">
          <Sparkles className="w-3 h-3" /> Démarchage · Partage express
        </div>
        <h1
          className="text-4xl sm:text-5xl font-black text-foreground tracking-tight"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          {title || 'Ma carte de visite'}
        </h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Montrez le QR à un prospect ou envoyez-lui le lien par SMS, WhatsApp ou email. Chaque interaction est tracée.
        </p>
      </div>

      {/* QR + Actions */}
      <section className="rounded-3xl border border-orange-500/30 bg-gradient-to-br from-orange-500/[0.08] via-orange-500/[0.02] to-transparent p-5 sm:p-7 mb-6">
        <div className="grid md:grid-cols-[280px_1fr] gap-6 items-start">
          {/* QR principal */}
          <div className="text-center">
            <div
              ref={qrRef}
              className="inline-block bg-white rounded-2xl p-4 shadow-2xl shadow-orange-500/10"
              data-testid="ma-vcard-qr"
            >
              <QRCodeCanvas value={vcardUrl} size={220} level="M" includeMargin={false} />
            </div>
            <div className="mt-3 flex items-center justify-center gap-2 text-[11px] text-emerald-400 font-bold uppercase tracking-widest">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              Actif · Prête à partager
            </div>
          </div>

          {/* Actions + URL */}
          <div className="min-w-0 space-y-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">
                URL publique de ma carte
              </div>
              <div className="flex items-center gap-2">
                <div
                  className="flex-1 min-w-0 rounded-lg border border-white/15 bg-black/40 px-3 py-2.5 text-[13px] text-foreground truncate font-mono"
                  data-testid="ma-vcard-url"
                >
                  {vcardUrl}
                </div>
              </div>
            </div>

            {/* Actions principales */}
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={share}
                data-testid="ma-vcard-share-btn"
                className="inline-flex items-center justify-center gap-2 px-4 h-11 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold transition shadow-lg shadow-orange-900/30"
              >
                <Share2 className="w-4 h-4" /> Partager
              </button>
              <button
                onClick={copyLink}
                data-testid="ma-vcard-copy-btn"
                className="inline-flex items-center justify-center gap-2 px-4 h-11 rounded-xl border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] text-foreground text-sm font-bold transition"
              >
                {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copié !' : 'Copier le lien'}
              </button>
              <a
                href={vcardUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="ma-vcard-open-btn"
                className="inline-flex items-center justify-center gap-2 px-4 h-11 rounded-xl border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] text-foreground text-sm font-bold transition"
              >
                <ExternalLink className="w-4 h-4" /> Ouvrir
              </a>
              <button
                onClick={downloadQr}
                data-testid="ma-vcard-download-btn"
                className="inline-flex items-center justify-center gap-2 px-4 h-11 rounded-xl border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] text-foreground text-sm font-bold transition"
              >
                <Download className="w-4 h-4" /> Télécharger QR
              </button>
            </div>

            <button
              onClick={printCard}
              data-testid="ma-vcard-print-btn"
              className="w-full inline-flex items-center justify-center gap-2 px-4 h-10 rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.06] text-muted-foreground text-xs font-semibold transition"
            >
              <Printer className="w-3.5 h-3.5" /> Imprimer un format A6 (poche)
            </button>
          </div>
        </div>
      </section>

      {/* Stats + Liens trackés */}
      <section className="grid sm:grid-cols-4 gap-3 mb-6">
        <StatCard icon={Contact} label="Liens créés" value={stats?.count ?? '—'} color="#f59e0b" />
        <StatCard icon={Eye} label="Visites" value={stats?.visits ?? '—'} color="#1d4ed8" />
        <StatCard icon={Phone} label="Appels" value={stats?.calls ?? '—'} color="#059669" />
        <StatCard icon={CalendarIcon} label="Réservations" value={stats?.reserves ?? '—'} color="#dc2626" />
      </section>

      {/* Prospects les plus actifs */}
      {recent.length > 0 && (
        <section className="mb-6" data-testid="ma-vcard-recent">
          <div className="flex items-center justify-between mb-3">
            <h2
              className="text-lg font-black text-foreground"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Prospects les plus actifs
            </h2>
            <Link
              to="/admin/vcard-tracking"
              className="text-xs font-semibold text-orange-400 hover:text-orange-300 inline-flex items-center gap-1"
            >
              Tous les liens trackés <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="space-y-2">
            {recent.map((p) => (
              <RecentRow key={p.slug} prospect={p} />
            ))}
          </div>
        </section>
      )}

      {/* Astuces d'usage */}
      <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 sm:p-5">
        <div className="text-[10.5px] font-black uppercase tracking-[0.22em] text-muted-foreground mb-3 flex items-center gap-2">
          <Sparkles className="w-3 h-3" /> Comment l'utiliser en démarchage
        </div>
        <div className="grid sm:grid-cols-3 gap-3 text-xs">
          <TipCard icon={QrCode} title="Face-à-face">
            Montrez votre écran, le prospect scanne avec son appareil photo. Il arrive sur votre carte.
          </TipCard>
          <TipCard icon={Share2} title="SMS / WhatsApp">
            « Partager » ouvre l'app native de votre téléphone. Un simple message avec le lien.
          </TipCard>
          <TipCard icon={BarChart3} title="Tracking par prospect">
            Pour mesurer qui scanne, créez un lien unique dans « Carte de visite » (menu).
          </TipCard>
        </div>
      </section>
    </div>
  );
}

// ──────────────────────────────────────────────────────── Sous-composants
function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center mb-2"
        style={{ background: `${color}22`, color }}
      >
        <Icon size={15} />
      </div>
      <div
        className="text-2xl sm:text-3xl font-black text-foreground"
        style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
      >
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mt-1">
        {label}
      </div>
    </div>
  );
}

function RecentRow({ prospect }) {
  const total = (prospect?.stats?.visits || 0) + (prospect?.stats?.clicks || 0);
  const slug = prospect.slug;
  return (
    <Link
      to="/admin/vcard-tracking"
      className="flex items-center gap-3 p-3 rounded-xl border border-border bg-card hover:bg-muted transition"
    >
      <div className="w-9 h-9 rounded-lg bg-orange-500/15 text-orange-500 flex items-center justify-center flex-shrink-0">
        <Contact size={15} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-foreground text-sm font-bold truncate">
          {prospect.prospect_name || 'Prospect sans nom'}
        </div>
        <div className="text-muted-foreground text-xs truncate font-mono">
          /b/{slug}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-xl font-black text-foreground" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          {total}
        </div>
        <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold">
          interactions
        </div>
      </div>
    </Link>
  );
}

function TipCard({ icon: Icon, title, children }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5 text-orange-400" />
        <div className="text-xs font-black text-foreground">{title}</div>
      </div>
      <p className="text-[11px] text-muted-foreground leading-relaxed">{children}</p>
    </div>
  );
}
